import * as THREE from 'three';
import { GeometryBuilder } from './geometry-builder';
export const MILITARY_FAMILIES = ['military-sedan', 'humvee', 'apc', 'tank', 'troop-truck'] as const;
export type MilitaryVehicleKind = (typeof MILITARY_FAMILIES)[number];
export const MILITARY_SPECS: Record<
  MilitaryVehicleKind,
  { name: string; width: number; length: number; height: number; armour: number; seats: number; gun: number }
> = {
  'military-sedan': {
    name: 'Garrison staff sedan',
    width: 1.94,
    length: 4.9,
    height: 1.7,
    armour: 28,
    seats: 4,
    gun: 0,
  },
  humvee: {
    name: 'Alder armoured patrol',
    width: 2.5,
    length: 5.2,
    height: 2.45,
    armour: 66,
    seats: 5,
    gun: 2.17,
  },
  apc: {
    name: 'Cedar wheeled carrier',
    width: 2.9,
    length: 7.2,
    height: 2.8,
    armour: 120,
    seats: 10,
    gun: 2.52,
  },
  tank: {
    name: 'Juniper tracked defender',
    width: 3.35,
    length: 7.8,
    height: 2.75,
    armour: 190,
    seats: 3,
    gun: 1.95,
  },
  'troop-truck': {
    name: 'Field transport and relief truck',
    width: 2.5,
    length: 8,
    height: 3.25,
    armour: 36,
    seats: 20,
    gun: 0,
  },
};
export const isMilitaryVehicle = (kind: string): kind is MilitaryVehicleKind => kind in MILITARY_SPECS;
/** Original assembled silhouettes. White panels receive faction tint; dark
 * tyres, windows, lamps and equipment keep their local material colours. */
export function militaryVehicleGeometry(kind: MilitaryVehicleKind): THREE.BufferGeometry {
  const g = new GeometryBuilder(),
    s = MILITARY_SPECS[kind],
    w = s.width,
    L = s.length;
  const box = (x: number, y: number, z: number, a: number, b: number, c: number, col = 0xffffff) =>
    g.box(x, y, z, a, b, c, col);
  box(-w / 2 + 0.12, 0.42, -L / 2 + 0.08, w - 0.24, 0.36, L - 0.16, 0x535f59);
  if (kind === 'tank') {
    for (const x of [-w / 2, w / 2 - 0.55]) {
      box(x, 0.16, -L / 2 + 0.2, 0.55, 0.87, L - 0.4, 0x39423e);
      for (let z = -L / 2 + 0.22; z < L / 2 - 0.2; z += 0.25)
        box(x - 0.02, 0.13, z, 0.59, 0.14, 0.11, 0x67716a);
      for (let i = 0; i < 7; i++) box(x + 0.05, 0.3, -L / 2 + 0.5 + i * 0.98, 0.45, 0.53, 0.62, 0x7b8376);
    }
    box(-w / 2 + 0.42, 0.8, -L / 2 + 0.55, w - 0.84, 0.69, L - 1.1);
    box(-1.04, 1.49, -1.8, 2.08, 0.33, 3.6);
    for (let i = 0; i < 6; i++) box(-0.78 + i * 0.27, 1.51, 2.3, 0.12, 0.13, 0.78, 0x58685b);
  } else {
    const axles = kind === 'apc' || kind === 'troop-truck' ? [-0.34, 0.12, 0.34] : [-0.32, 0.32];
    for (const side of [-1, 1])
      for (const z of axles) {
        const tyre = new THREE.CylinderGeometry(
          kind === 'military-sedan' ? 0.35 : 0.48,
          kind === 'military-sedan' ? 0.35 : 0.48,
          0.32,
          10,
        );
        tyre.rotateZ(Math.PI / 2);
        tyre.translate(side * (w / 2 - 0.14), kind === 'military-sedan' ? 0.36 : 0.49, L * z);
        const pos = tyre.getAttribute('position'),
          normal = tyre.getAttribute('normal'),
          idx = tyre.index!;
        for (let i = 0; i < idx.count; i += 3) {
          const p = [0, 1, 2].map((j) => new THREE.Vector3().fromBufferAttribute(pos, idx.getX(i + j)));
          g.triangle(
            p[0],
            p[1],
            p[2],
            new THREE.Vector3().fromBufferAttribute(normal, idx.getX(i)),
            0x35413c,
          );
        }
        tyre.dispose();
        box(side < 0 ? -w / 2 - 0.015 : w / 2 - 0.035, 0.3, L * z - 0.15, 0.05, 0.28, 0.3, 0xa1aaa0);
      }
    if (kind === 'troop-truck') {
      box(-w / 2 + 0.08, 0.85, -L / 2 + 0.1, w - 0.16, 1.87, 2.08);
      box(-w / 2 + 0.1, 1.02, -L / 2 + 2.32, w - 0.2, 0.4, L - 2.52);
      for (const x of [-w / 2 + 0.12, w / 2 - 0.2]) {
        box(x, 1.4, -L / 2 + 2.32, 0.08, 1.15, L - 2.52);
        for (let i = 0; i < 5; i++) box(x - 0.01, 1.5, -L / 2 + 2.55 + i * 0.99, 0.1, 1.35, 0.085, 0x667866);
      }
      box(-w / 2 + 0.1, 2.9, -L / 2 + 2.29, w - 0.2, 0.17, L - 2.48, 0xc1c4ad);
      box(-w / 2 + 0.11, 1.42, L / 2 - 0.18, w - 0.22, 0.85, 0.1);
    } else if (kind === 'apc') {
      box(-w / 2 + 0.12, 0.95, -L / 2 + 0.15, w - 0.24, 1.48, L - 0.3);
      box(-w / 2 + 0.2, 2.43, -L / 2 + 0.8, w - 0.4, 0.15, L - 1.65);
      for (let i = 0; i < 4; i++)
        for (const x of [-w / 2 + 0.07, w / 2 - 0.1])
          box(x, 1.75, -1.1 + i * 0.9, 0.04, 0.17, 0.27, 0x314b50);
      box(-0.78, 1.08, L / 2 - 0.11, 1.56, 1.3, 0.1, 0x839484);
    } else {
      const sedan = kind === 'military-sedan',
        top = sedan ? 1.57 : 2.09;
      box(-w / 2 + 0.12, 0.78, -L / 2 + 0.07, w - 0.24, 0.43, L - 0.14);
      // Hollow cabin: actual occupants sit between sill panels and pillars.
      for (const x of [-w / 2 + 0.19, w / 2 - 0.27]) {
        box(x, 1.2, -1.05, 0.08, 0.2, 2.82);
        for (const z of [-1.05, 0.08, 1.67]) box(x, 1.4, z, 0.08, top - 1.4, 0.1);
      }
      box(-w / 2 + 0.27, 1.2, 1.67, w - 0.54, top - 1.2, 0.1);
      box(-w / 2 + 0.27, 1.2, -1.05, w - 0.54, 0.3, 0.1);
      // Window openings keep the seated original occupants visible from above.
      for (const x of [-w / 2 + 0.15, w / 2 - 0.2])
        for (const z of [-0.83, 0.29]) {
          box(x, 1.42, z, 0.055, sedan ? 0.23 : 0.45, 0.86, 0x577c7b);
          box(x - 0.015, 1.12, z + 0.1, 0.075, 0.08, 0.28, 0xc7cdb7);
        }
      if (sedan) box(-w / 2 + 0.22, top, -1.12, w - 0.44, 0.12, 2.94);
      else {
        // Four roof panels leave the gunner's hatch physically open in the mesh.
        box(-w / 2 + 0.22, top, -1.12, w - 0.44, 0.12, 1);
        box(-w / 2 + 0.22, top, 0.98, w - 0.44, 0.12, 0.84);
        box(-w / 2 + 0.22, top, -0.12, 0.55, 0.12, 1.1);
        box(0.48, top, -0.12, 0.55, 0.12, 1.1);
      }
      if (!sedan) {
        // Open roof ring surrounds the real gunner position.
        for (const x of [-0.7, 0.48]) box(x, 2.22, -0.12, 0.22, 0.15, 1.1);
        for (const z of [-0.12, 0.76]) box(-0.48, 2.22, z, 0.96, 0.15, 0.22);
        box(-0.53, 0.97, L / 2 - 0.03, 1.06, 1.06, 0.27, 0x394a40);
        box(0.74, 1.22, L / 2 - 0.04, 0.3, 0.8, 0.3, 0xa7af95);
      }
    }
    if (kind === 'humvee' || kind === 'military-sedan') {
      const top = kind === 'humvee' ? 2.09 : 1.57;
      // The windshield belongs to the cabin pillars; the old common front
      // panel floated over the hood of every short-cab vehicle.
      box(-w / 2 + 0.27, 1.48, -1.061, w - 0.54, top - 1.48, 0.055, 0x587c7e);
      box(-w / 2 + 0.19, 1.4, -1.08, w - 0.38, 0.085, 0.12);
      box(-0.025, 1.48, -1.075, 0.05, top - 1.48, 0.08);
      for (const side of [-1, 1]) {
        const x = side < 0 ? -w / 2 - 0.035 : w / 2 - 0.205;
        box(x, 1.43, -0.96, 0.24, 0.06, 0.08, 0x53625a);
        box(side < 0 ? x : x + 0.16, 1.4, -1.04, 0.075, 0.2, 0.16, 0x394644);
      }
    } else box(-w / 2 + 0.27, 1.33, -L / 2 + 0.035, w - 0.54, 0.45, 0.045, 0x587c7e);
    for (const x of [-w / 2 + 0.16, w / 2 - 0.49]) box(x, 0.91, -L / 2, 0.33, 0.18, 0.07, 0xf2e8b1);
    box(-w / 2 + 0.07, 0.64, -L / 2 - 0.035, w - 0.14, 0.15, 0.16, 0x66796c);
  }
  for (const x of [-w / 2 + 0.02, w / 2 - 0.07]) {
    box(x, 0.94, L / 2 - 0.13, 0.05, 0.22, 0.15, 0xb86f57);
    box(x, 1.12, 0.15, 0.06, 0.15, 0.55, 0xd9dcc4);
  }
  const mesh = g.geometry();
  mesh.computeBoundingBox();
  return mesh;
}
export function militaryTurretGeometry(kind: MilitaryVehicleKind): THREE.BufferGeometry {
  const g = new GeometryBuilder();
  if (kind === 'tank') {
    g.box(-0.96, 0, -1.25, 1.92, 0.65, 2.25, 0xffffff);
    g.box(-0.62, 0.65, -0.53, 1.24, 0.13, 1.08, 0xd1d3ba);
    g.box(-0.14, 0.26, -4.05, 0.28, 0.25, 3.2, 0x94a18d);
    g.box(-0.2, 0.24, -4.16, 0.4, 0.29, 0.37, 0x667969);
    for (const x of [-1.02, 0.88]) g.box(x, 0.18, -0.91, 0.14, 0.33, 0.73, 0x4c6250);
  } else if (kind === 'apc' || kind === 'humvee') {
    g.box(-0.35, 0, -0.37, 0.7, 0.22, 0.74, 0x637a66);
    g.box(-0.1, 0.22, -0.63, 0.2, 0.24, 0.75, 0x38463f);
    g.box(-0.042, 0.29, -1.16, 0.084, 0.09, 0.66, 0x343e3b);
    g.box(-0.33, 0.14, -0.08, 0.2, 0.28, 0.3, 0xa6ad89);
    g.box(-0.44, 0.2, -0.43, 0.17, 0.46, 0.12, 0xffffff);
    g.box(0.27, 0.2, -0.43, 0.17, 0.46, 0.12, 0xffffff);
  }
  const mesh = g.geometry();
  mesh.computeBoundingBox();
  return mesh;
}
